import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  predictionManagerAxiosApi,
  notificationsAxiosApi,
} from 'src/Config/axiosApi';
import type {
  ActivatableMatch,
  MatchEventFrame,
  UpsertMatchDto,
} from '../types/matchEvents.types';

/**
 * Control-plane REST for the Live Match Events tab (frozen contract §REST,
 * prediction-manager base path `/match-events`). Re-uses the existing
 * `predictionManagerAxiosApi` (shared Bearer + 401-refresh interceptors).
 * Mirrors `useGroups.ts` — a GET list query + activate/deactivate/upsert
 * mutations that invalidate the list.
 */

const MATCHES_KEY = 'match-events-matches';

/**
 * `GET /match-events/history/:fixtureId` (notifications-websocket) — the durable
 * match_events history (24h TTL). Loaded when a fixture is selected so a page
 * refresh rebuilds the FULL feed instead of only the ~20-min live replay.
 */
export function useMatchEventsHistory(fixtureId: number | null) {
  return useQuery<MatchEventFrame[]>(
    ['match-events-history', fixtureId],
    async () => {
      const { data } = await notificationsAxiosApi.get<MatchEventFrame[]>(
        `/match-events/history/${fixtureId}`,
      );
      return Array.isArray(data) ? data : [];
    },
    {
      enabled: fixtureId != null,
      staleTime: 15_000,
      refetchOnWindowFocus: false,
    },
  );
}

/** `GET /match-events/matches` — the activatable-match list (+ `active`). */
export function useMatchEventsMatches() {
  return useQuery<ActivatableMatch[]>(
    [MATCHES_KEY],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<
        ActivatableMatch[] | { matches: ActivatableMatch[] }
      >('/match-events/matches');
      // Canonical wire shape is a bare array; the `{ matches }` branch is
      // defensive against an enveloped payload.
      return Array.isArray(data) ? data : (data?.matches ?? []);
    },
    { refetchInterval: 15_000, keepPreviousData: true },
  );
}

/** `POST /match-events/matches` — upsert on `fixtureId`. */
export function useUpsertMatch() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: UpsertMatchDto) => {
      const { data } = await predictionManagerAxiosApi.post<ActivatableMatch>(
        '/match-events/matches',
        dto,
      );
      return data;
    },
    {
      mutationKey: 'match-events:upsert',
      onSuccess: () => {
        qc.invalidateQueries([MATCHES_KEY]);
      },
    },
  );
}

/** `POST /match-events/matches/:fixtureId/activate`. */
export function useActivateMatch() {
  const qc = useQueryClient();
  return useMutation(
    async (fixtureId: number) => {
      const { data } = await predictionManagerAxiosApi.post(
        `/match-events/matches/${fixtureId}/activate`,
      );
      return data;
    },
    {
      mutationKey: 'match-events:activate',
      onSuccess: () => {
        qc.invalidateQueries([MATCHES_KEY]);
      },
    },
  );
}

/** `POST /match-events/matches/:fixtureId/deactivate`. */
export function useDeactivateMatch() {
  const qc = useQueryClient();
  return useMutation(
    async (fixtureId: number) => {
      const { data } = await predictionManagerAxiosApi.post(
        `/match-events/matches/${fixtureId}/deactivate`,
      );
      return data;
    },
    {
      mutationKey: 'match-events:deactivate',
      onSuccess: () => {
        qc.invalidateQueries([MATCHES_KEY]);
      },
    },
  );
}

/** `DELETE /match-events/matches/:fixtureId` — deactivate + remove doc. */
export function useDeleteMatch() {
  const qc = useQueryClient();
  return useMutation(
    async (fixtureId: number) => {
      const { data } = await predictionManagerAxiosApi.delete(
        `/match-events/matches/${fixtureId}`,
      );
      return data;
    },
    {
      mutationKey: 'match-events:delete',
      onSuccess: () => {
        qc.invalidateQueries([MATCHES_KEY]);
      },
    },
  );
}
